/**
 * Created by rhinov15 on 29/07/2016.
 */
/**
 *
 * Global state to add an entity to SavaneJS and display the corresponding Node in Cocos
 *
 * @param id
 * @constructor
 */

import { ActionState, ActionStateEnum, IdleConstruction, IdleCameraState, IdleDecoration } from "..";
import { EntityManager } from "../../plan/EntityManager";
import { EntityNode } from "../../plan/EntityNodes/EntityNode";
import { PlanManager } from "../../../../cocosPlan";
import { ArrangementGroup, arrangementManager, ArrangementObject, Commands, Entity, EntityFactory, Events, eventsManager, Listener, math, planManager, RenderCamera, roomManager, SavaneConstants, SceneConstants, TechnicalElement } from "@rhinov/savane-js";


declare var cc: any;

declare var $: any;

export class AddEntityState extends ActionState {

    // Store state name from global enum
    state: number = ActionStateEnum.AddEntity;
    entity: Entity;
    node: EntityNode;
    _hasMoved: boolean = false;
    entityPut: boolean = false;
    fromdeco: boolean = false;

    shiftPressed: boolean = false;
    altPressed: boolean = false;
    cPressed: boolean = false;
    duplicate: boolean = false;
    floorChanger: Listener | null;

    constructor(entity: Entity, duplicate?: boolean, fromdeco?: boolean) {
        super();

        // Déactivate any selected item previously since it might disturb the new object placement
        PlanManager.getInstance().updateItemsSelected(null, false, false);

        // Create SavaneJS entity
        this.entity = entity;
        if (this.entity.startTemporary) {
            this.entity.startTemporary();
        }

        // Create a temp node
        this.node = this.createNode(this.entity);
        this.node.setVisible(false);
        this.node.setSelected(true);


        this.duplicate = duplicate ?? false;
        if (fromdeco !== undefined) {
            this.fromdeco = fromdeco;
        }

        this.floorChanger = eventsManager.instance.addListener(Events.PLAN_FLOOR_CHANGED, (): void => {
            //Change state
            eventsManager.instance.dispatch(Events.CHANGE_STATE, this.createIdleState(this.entity));
        });
    }

    fromVoiceRecognition(cursorPosition: math.vec3) {
        this.moveMouseCursor(cursorPosition);
    }

    getAction(): number {
        return this.state;
    }

    getEntityType(): number {
        return this.entity.entityType!;
    }

    mouseClickCancelled(cursorPosition: math.vec3) : ActionState | null {
        PlanManager.getInstance().updateItemsSelected(null);

        var newState = this.createIdleState(this.entity);
        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, newState.getAction());
        return newState;
    }

    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean) {
        if (keyCode == cc.KEY.escape) {
            PlanManager.getInstance().updateItemsSelected(null);

            var newState = this.createIdleState(this.entity);
            eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, newState.getAction());
            return newState;
        }

        if (keyCode == cc.KEY.shift) {
            this.shiftPressed = true;
        }
        if (keyCode == cc.KEY.alt || alt) {
            this.altPressed = true;
        }
        if (keyCode === cc.KEY.s) {
            PlanManager.getInstance().toggleSnap();
        }
        if (keyCode === cc.KEY.c) {
            this.cPressed = true;
        }
        return null;
    }

    keyUnPressed(keyCode: number, ctrl?: boolean, maj?: boolean) {
        if (keyCode == cc.KEY.shift) {
            this.shiftPressed = false;
            this.duplicate = false;
        }
        if (keyCode == cc.KEY.alt) {
            this.altPressed = false;
        }
        if (keyCode === cc.KEY.c) {
            this.cPressed = false;
        }

        return null;
    }

    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        if (mouseButton === 2) {
            PlanManager.getInstance().mouseClickCancelled();
        }

        return null;
    }

    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        this.entityPut = true;
        eventsManager.instance.dispatch(Events.TOOL_UNSELLECT, null);

        var valid = this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!);

        if ((this.entity.entityType === SceneConstants.EntityType.TechnicalElement) && ((this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.pole) && (this.altPressed)) {
            valid = true;
        }

        if (valid && !this.entity.isGhost()) {
            var parent = null;
            if (this.entity.isArrangementObjectEntity() || this.entity.isArrangementGroupEntity() ||
                this.entity.isRenderCameraEntity() || this.entity.isGeometryPrimitiveEntity())
            {
                //Get room
                parent = roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);
            }
            var roomFloorHeight = 0;
            if (parent) {
                roomFloorHeight = parent.floorHeight;
            }
            //No parent found
            if (parent === null) {
                parent = PlanManager.getInstance().world.currentScene!.currentFloor!;
            }
            if (this.entity.isRenderCameraEntity()) {
                (this.entity as RenderCamera).updateCameraNb(PlanManager.getInstance().world);
            }
            let floor: any = parent;
            while (floor && floor.isFloorEntity && floor.isFloorEntity() === false) {
                floor = floor.parent;
            }
            if (floor) {
                if (this.entity.isRenderCameraEntity()) {
                    if (floor.height < 0) {
                        (this.entity as RenderCamera).height = floor.height +(this.entity as RenderCamera).height + roomFloorHeight;
                    } else {
                        (this.entity as RenderCamera).height += floor.height + roomFloorHeight;
                    }
                }
                else {
                    if (floor.height < 0) {
                        this.entity.floorHeight = floor.height + this.entity.floorHeight + roomFloorHeight;
                    } else {
                        this.entity.floorHeight += floor.height + roomFloorHeight;
                    }
                }
            }

            PlanManager.getInstance().executeCommand(new Commands.AddEntityCommand(this.entity, parent, true, false));
            PlanManager.getInstance().updateItemsSelected([this.entity], false, true);

            if (!this.shiftPressed && !this.duplicate) {
                var newState = this.createIdleState(this.entity);
                $('#gameCanvas').focus();
                eventsManager.instance.dispatch(Events.CHANGE_STATE, newState);
            }
            else {
                eventsManager.instance.dispatch(Events.CHANGE_STATE, new AddEntityState(EntityFactory.getInstance().cloneEntity(this.entity, true)!, true));
            }
        }
        else {
            if (this.entity.isGhost()) {
                console.error("Can't add ghost entities");
            }
            this.node.removeFromParent();
            this.removeNode(this.entity);
            return this.createIdleState(this.entity);
        }

        return null;
    }

    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        eventsManager.instance.dispatch(Events.STOP_UPDATING_ENVS);
        super.moveMouseCursor(cursorPosition, hoverEntities, planpos);
        var savedPosition = this.entity.position;

        // Redraw Node
        var node = EntityManager.getInstance().getNode(this.entity);
        if (!node) {
            return null;
        }
        node.needRedraw = true;

        $('#gameCanvas').focus();

        this._hasMoved = true;
        this.node.setVisible(this._hasMoved);
        //Move entity
        var newPosition = math.vec3.create();
        math.vec3.set(newPosition, cursorPosition[0], cursorPosition[1], this.entity.position[2]);
        this.entity.position = newPosition;

        if (this.entity.entityType === SceneConstants.EntityType.ArrangementObject) {
            (this.entity as ArrangementObject).updateFloorHeightFromAnchor(roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!)!, PlanManager.getInstance().world.currentScene!.currentFloor!);

            if (this.cPressed) {
                var arrangements = arrangementManager.getArrangementObjectsAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

                for (var i = 0; i < arrangements.length; i++) {
                    if (Math.abs(arrangements[i].floorHeight + arrangements[i].height - this.entity.floorHeight) < SavaneConstants.PositionTolerance) {
                        var pos = math.vec3.create();
                        math.vec3.set(pos, arrangements[i].position[0], arrangements[i].position[1], this.entity.position[2]);

                        this.entity.angle = arrangements[i].angle;
                        this.entity.position = pos;
                    }
                }
            }
        }

        //Update node
        switch (this.entity.entityType) {
            //List of snappable types
            case SceneConstants.EntityType.ArrangementGroup:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.Staircase:
            case SceneConstants.EntityType.TechnicalElement:
            case SceneConstants.EntityType.GeometryPrimitive:
                if (PlanManager.getInstance().snapEnabled) {
                    if (this.state !== ActionStateEnum.FrontViewAddEntity || !this.entity.isArrangementGroupEntity()) {
                        planManager.snapArrangement((this.entity as ArrangementGroup), PlanManager.getInstance().world.currentScene!.currentFloor!);
                    }

                    if (!this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                        this.entity.position = savedPosition;
                        if (this.entity.temporary !== null) {
                            this.entity.temporary.recomputeValidity = true;
                        }
                    }
                }
                else {
                    if (this.entity.temporary !== null) {
                        this.entity.temporary.recomputeValidity = true;
                    }
                }

                if (((this.entity as TechnicalElement).objectId !== SceneConstants.TechnicalElementType.pole) || (!this.altPressed)) {
                    this.node.setValidity(this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!));
                }
                break;
        }
        this.node.update(undefined);

        return null;
    }

    snapClick() {
        return null;//this.createIdleState(this.entity);
    }
    /**
     *
     * Create corresponding idle state for that type of entity
     *
     * @param entity
     * @returns {*}
     */
    createIdleState(entity: Entity) : ActionState {
        var state: ActionState;

        switch (entity.entityType) {
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementGroup:
            case SceneConstants.EntityType.ArrangementObject:
                state = new IdleDecoration();
                break;
            case SceneConstants.EntityType.UserPicture:
                state = new IdleCameraState();
                break;
            case SceneConstants.EntityType.GeometryPrimitive:
                if (this.fromdeco) {
                    state = new IdleDecoration();
                } else {
                    state = new IdleConstruction();
                }
                break;
            default:
                state = new IdleConstruction();
                break;
        }
        return state;
    }

    createNode(entity: Entity): EntityNode {
        const node = EntityManager.getInstance().createNode(entity, true);
        if (!node) {
            throw new Error('Failed to create node for entity');
        }
        return node;
    }
    removeNode(entity: Entity) {
        //EntityManager.getInstance().deleteNode(entity.id);
        EntityManager.getInstance().deleteNodeAndChild(entity);
    }

    isDecorationState(): boolean {
        switch (this.entity.entityType) {
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
                return (true);
                break;
        }
        return (false);
    }

    dispose() {
        if (this.floorChanger !== null) {
            eventsManager.instance.removeListener(this.floorChanger);
        }
        if (!this.entityPut) {
            //Leave without putting down entity to cancel move
            this.node.removeFromParent();
            this.removeNode(this.entity);
        }
    }
}

